import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Field, getIn } from 'formik';

import moment, { Moment } from 'moment';
import Datetime from 'react-datetime';
import useTimeout from '../../lib/hooks/useTimeout';

type InputProps = {
  type?: string;
  name: string;
  value: string;
  errors: string | string[] | any;
  hasError: boolean | any;
  placeholder?: string;
  className?: string;
  handleBlur: (e: React.FocusEvent) => void;
  handleChange: (e: React.ChangeEvent) => void;
  pillBg?: string;
};

export const TextInput: React.FC<InputProps> = (props) => {
  return (
    <div className={`form-group ${props.className || ''}`}>
      <input
        type={props.type || 'text'}
        name={props.name}
        value={props.value}
        onBlur={props.handleBlur}
        onChange={props.handleChange}
        placeholder={props.placeholder || ''}
        className={`py-4 px-6 ${props.hasError ? 'is-invalid' : ''}`}
      />
      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

export const TextAreaInput: React.FC<InputProps> = (props) => {
  return (
    <div className={`form-group ${props.className || ''}`}>
      <textarea
        name={props.name}
        value={props.value}
        onBlur={props.handleBlur}
        onChange={props.handleChange}
        placeholder={props.placeholder || ''}
        className={`py-4 px-6 ${props.hasError ? 'is-invalid' : ''}`}
      />
      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

export const TextInputWithPill: React.FC<InputProps> = (props) => {
  return (
    <div className="form-group">
      <div
        className={`form-group form-group--pill h-full-r flex p-1 overflow-hidden bg-gray-300 rounded-full ${props.hasError ? 'is-invalid' : ''
          }`}
      >
        <div
          className={`flex items-center justify-center flex-grow-0 flex-shrink-0 text-white text-sm p-3p px-4 w-24 rounded-full ${props.pillBg || 'bg-secondary'
            }`}
        >
          +254
        </div>

        <input
          type={props.type || 'text'}
          name={props.name}
          value={props.value}
          onBlur={props.handleBlur}
          onChange={props.handleChange}
          placeholder={props.placeholder || ''}
          className={`px-4 py-3 flex-1`}
        />
      </div>

      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

type InputButtonProps = {
  type?: string;
  name: string;
  value: string;
  errors: string | string[] | any;
  hasError: boolean | any;
  placeholder?: string;
  className?: string;
  btnText?: string;
  handleBlur: (e: React.FocusEvent) => void;
  handleChange: (e: React.ChangeEvent) => void;
  pillBg?: string;
  onClick: () => void;
  loading: boolean;
  loadingError: boolean;
  isDisabled: boolean;
};

export const TextInputWithButton: React.FC<InputButtonProps> = ({ btnText = 'Get OTP', ...props }) => {
  const [seconds, setSeconds] = React.useState(30);
  const [isFetching, setIsFetching] = useState(false);

  const onButtonClick = () => {
    props.onClick();
    setIsFetching(true);
  };

  useTimeout(
    () => {
      setSeconds(seconds - 1);
    },
    1000,
    isFetching,
  );

  useEffect(() => {
    if (props.loadingError) {
      setIsFetching(false);
      setSeconds(30);
    }
  }, [props.loadingError]);

  useEffect(() => {
    if (seconds < 2) {
      setIsFetching(false);
      setSeconds(30);
    }
  }, [seconds]);

  const isSuccess = !props.loading && !props.loadingError;

  return (
    <div className="form-group">
      <div
        className={`form-group form-group--pill h-full-r flex p-1 overflow-hidden bg-gray-300 rounded-full ${props.hasError ? 'is-invalid' : ''
          }`}
      >
        <input
          type={props.type || 'text'}
          name={props.name}
          value={props.value}
          onBlur={props.handleBlur}
          onChange={props.handleChange}
          placeholder={props.placeholder || ''}
          className={`px-4 py-3 flex-1`}
        />

        <button
          type="button"
          onClick={onButtonClick}
          disabled={isFetching || props.isDisabled}
          className={`flex items-center flex-grow-0 flex-shrink-0 text-white text-sm p-3p px-4 w-32 rounded-full focus:bg-blue-500 ${props.pillBg || 'bg-secondary'
            } ${isFetching ? (isSuccess ? 'opacity-75' : 'opacity-50') : ''} ${props.isDisabled ? 'opacity-50' : ''
            }`}
        >
          <div
            className={`w-full h-full flex items-center rounded-full ${isFetching
                ? isSuccess
                  ? 'justify-center'
                  : 'bg-loading-1 justify-start'
                : 'justify-center'
              }`}
          >
            {isFetching ? (isSuccess ? `${btnText} in ${seconds}` : btnText) : btnText}
          </div>
        </button>
      </div>

      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

type FilePickerInputProps = {
  name: string;
  value: string;
  accept: string;
  errors: string | string[] | any;
  hasError: boolean | any;
  placeholder?: string;
  className?: string;
  pickerName: string;
  handleBlur: (e: React.FocusEvent) => void;
  onFileChange: (e: React.ChangeEvent) => void;
  pillBg?: string;
  multiple?: boolean;
};

export const FilePickerInput: React.FC<FilePickerInputProps> = (props) => {
  const fileInput = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    fileInput.current.click();
  };

  return (
    <div className="w-full form-group">
      <div
        className={`form-group form-group--pill h-full-r flex p-1 overflow-hidden bg-gray-300 rounded-full ${props.hasError ? 'is-invalid' : ''
          }`}
      >
        <div
          className={`flex items-center justify-center flex-0 text-white text-sm p-3p px-5 pr-4 rounded-full ${props.pillBg || 'bg-black-primary'
            }`}
        >
          <button
            type="button"
            onClick={openFilePicker}
            className="w-full py-2 pr-12 font-medium text-justify text-white bg-transparent bg-upload-right focus:outline-none"
          >
            Upload Work
          </button>
        </div>

        <input
          type="text"
          readOnly
          name={props.name}
          value={props.value}
          onBlur={props.handleBlur}
          placeholder={props.placeholder || ''}
          className={`px-4 py-3 flex-1`}
        />

        <input
          className="hidden"
          type="file"
          name={props.pickerName}
          accept={props.accept}
          ref={fileInput}
          multiple={props.multiple}
          onChange={props.onFileChange}
        />
      </div>

      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

type SelectOption = {
  label: string;
  value: string;
};

type SelectInputProps = {
  value: string;
  name: string;
  options: SelectOption[];
  placeholder?: string;
  onChange: (name: string, value: string, validate?: boolean) => void;
  onBlur: (name: string, touched: boolean, validate?: boolean) => void;
  className?: string;
  errors: string | string[] | any;
  hasError: boolean | any;
  isLading?: boolean;
  loadingMessage?: string;
  noOptionsMessage?: string;
  isDisabled?: boolean;
};

const customStyles = {
  control: (provided, { isFocused }) => ({
    ...provided,
    backgroundColor: isFocused ? '#EBF8FF' : '#E2E8F0',
    border: isFocused ? 'none' : 'none',
    boxShadow: isFocused ? '0 0 0 2px rgba(66, 153, 225, 0.5) inset' : 'none',
    borderRadius: '50px',
    transition: 'none',
    borderWidth: '0',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '1rem 1.5rem',
    fontWeight: '500',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#4A5568',
    margin: 0,
  }),
  input: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    borderBottom: '1px solid #ddd',
    color: state.isSelected ? '#fff' : 'black',
    backgroundColor: state.isSelected ? '#503e9d' : 'white',
    '&:hover': { backgroundColor: '#e3dff7' },
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    paddingRight: '6px',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided, { isFocused }) => ({
    ...provided,
    color: isFocused ? '#fff' : '#fff',
    backgroundColor: '#212121',
    borderRadius: '50%',
    height: '44px',
    width: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }),
};

const customErrorStyles = {
  ...customStyles,
  control: (provided, { isFocused }) => ({
    ...provided,
    border: 'none',
    borderWidth: '0',
    transition: 'none',
    borderRadius: '50px',
    backgroundColor: isFocused ? '#EBF8FF' : '#E2E8F0',
    boxShadow: isFocused
      ? '0 0 0 2px rgba(66, 153, 225, 0.5) inset'
      : '0 0 0 2px rgba(255, 0, 0, 0.45) inset',
  }),
};

export const SelectInput: React.FC<SelectInputProps> = (props) => {
  const [selected, setSelected] = useState<SelectOption | string>('');

  const handleChange = (selected: SelectOption) => {
    props.onChange(props.name, selected.value, true);
  };

  const handleBlur = () => {
    props.onBlur(props.name, true, false);
  };

  useEffect(() => {
    if (props.value !== '' || props.value !== undefined) {
      const _values = props.options.filter((option) => option.value === props.value);

      if (_values.length > 0) {
        setSelected(_values[0]);
      } else {
        setSelected('');
      }
    }
  }, [props.value, props.options]);

  return (
    <div className={`form-group ${props.className || ''}`}>
      <Select
        value={selected}
        instanceId={`rs-${props.name}`}
        options={props.options.sort((a, b) => {
          if (a.label > b.label) {
            return 1;
          }
          if (b.label > a.label) {
            return -1;
          }
          return 0;
        })}
        onChange={handleChange}
        onBlur={handleBlur}
        isLoading={props.isLading || false}
        loadingMessage={() => props.loadingMessage || 'Loading...'}
        noOptionsMessage={() => props.noOptionsMessage || 'No Options match your criteria'}
        styles={props.hasError ? customErrorStyles : customStyles}
        placeholder={props.placeholder || ''}
        classNamePrefix="react-select"
        className="react-select-container"
        isDisabled={props.isDisabled}
      />

      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

type SubmitButtonProps = {
  type?: 'button' | 'submit' | 'reset';
  title: string;
  loadingMsg?: string;
  isDirty: boolean;
  isSubmitting: boolean;
  className?: string;
  buttonBg?: string;
  direction: 'right' | 'left';
  onClick?: () => void;
};

export const SubmitButton: React.FC<SubmitButtonProps> = (props) => {
  return (
    <div className={`form-group mt-4 ${props.className}`}>
      <div
        className={`rounded-full px-6 ${props.isSubmitting ? 'bg-black relative' : props.buttonBg || 'bg-black-primary'
          }`}
      >
        <button
          onClick={props.onClick ? props.onClick : null}
          disabled={!props.isDirty || props.isSubmitting}
          className={`w-full py-4 font-medium ${props.direction === 'right' ? 'bg-arrow-right text-justify' : 'bg-arrow-left text-right'
            } text-white ${props.isSubmitting ? 'bg-loading' : ''}`}
          type={props.type || 'submit'}
        >
          {props.isSubmitting ? props.loadingMsg || 'Loading...' : props.title}
        </button>
      </div>
    </div>
  );
};

export const Button: React.FC<SubmitButtonProps> = (props) => {
  return (
    <div className={`form-group mt-4 ${props.className}`}>
      <div
        className={`rounded-full px-6 ${props.isSubmitting ? 'bg-black relative' : props.buttonBg || 'bg-black-primary'
          }`}
      >
        <button
          onClick={props.onClick}
          disabled={!props.isDirty || props.isSubmitting}
          className={`w-full py-4 font-medium ${props.direction === 'right' ? 'bg-arrow-right text-justify' : 'bg-arrow-left text-right'
            } text-white ${props.isSubmitting ? 'bg-loading' : ''}`}
          type={props.type || 'submit'}
        >
          {props.isSubmitting ? props.loadingMsg || 'Loading...' : props.title}
        </button>
      </div>
    </div>
  );
};

type DatePickerInputProps = {
  value: Date | string | Moment;
  name: string;
  placeholder?: string;
  onChange: (name: string, value: string, validate?: boolean) => void;
  onBlur: (name: string, touched: boolean, validate?: boolean) => void;
  className?: string;
  errors: string | string[] | any;
  hasError: boolean | any;
};

export const DatePickerInput: React.FC<DatePickerInputProps> = (props) => {
  const [selected, setSelected] = useState<Date | string | Moment>('');

  const handleChange = (value: Moment) => {
    props.onChange(props.name, value.format(), true);
  };

  const handleBlur = () => {
    props.onBlur(props.name, true, false); // name, touched, shouldValidate
  };

  useEffect(() => {
    if (props.value !== '' || props.value !== undefined) {
      setSelected(moment(props.value));
    }
  }, [props.value]);

  const isValidDate = (current) => {
    return current.isBefore(moment());
  };

  return (
    <div className={`form-group ${props.className || ''}`}>
      <Datetime
        isValidDate={isValidDate}
        value={selected}
        inputProps={{
          readOnly: true,
          placeholder: props.placeholder || 'Choose Date',
          className: `py-4 px-6 bg-calendar-right ${props.hasError ? 'is-invalid' : ''}`,
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        closeOnSelect
        dateFormat="DD MMM YYYY"
        timeFormat={false}
        className="form-group"
      />

      {props.hasError && (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{props.errors}</p>
      )}
    </div>
  );
};

export const FormikErrorMessage = ({ name }) => (
  <Field name={name}>
    {({ form }) => {
      const error = getIn(form.errors, name);
      const touch = getIn(form.touched, name);

      return touch && error ? (
        <p className="pt-1 pb-2 pl-4 text-xs italic text-left text-red-600">{error}</p>
      ) : null;
    }}
  </Field>
);

type CheckBoxProps = {
  label: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const CheckBoxInput: React.FC<CheckBoxProps> = (props) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
    props.onChange(e);
  };

  return (
    <label className="flex items-center text-gray-800 cursor-pointer checkbox">
      <span className="checkbox__input">
        <input
          type="checkbox"
          name="checkbox"
          className="w-6 h-6"
          onChange={handleChange}
          checked={isChecked}
          value={props.label}
        />
        <span className="inline-flex w-6 h-6 mr-2 border-2 border-current checkbox__control">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              d="M1.73 12.91l6.37 6.37L22.79 4.59"
            />
          </svg>
        </span>
      </span>
      <span className="text-md radio__label">{props.label}</span>
    </label>
  );
};
